import { useQuery } from '@tanstack/react-query';

import { isPrometheusConfigured } from '@/shared/config/prometheus-config';
import { DEFAULT_QUERY_OPTIONS, useQueryWithBoundary } from '@/shared/lib/query';
import { monitoringKeys } from '@/shared/lib/query/queryKeys';

import {
  BYTES_TO_KB,
  getInstantValue,
  getRangeValues,
  getRangeValuesByPod,
  PROMETHEUS_QUERIES,
  QUERY_INTERVALS,
  RANGE_SECONDS,
  REFETCH_INTERVAL,
  STALE_TIME,
} from '@/features/monitoring/api';
import type { NetworkUsageData } from '@/features/monitoring/api';

import { assignOrderedPodSeriesColors } from './lib/assignOrderedPodSeriesColors';

export const useNetworkUsage = () => {
  const enabled = isPrometheusConfigured();

  const summaryQuery = useQueryWithBoundary<{
    receiveCurrent: number;
    transmitCurrent: number;
    unit: NetworkUsageData['unit'];
  }>({
    queryKey: monitoringKeys.network(),
    queryFn: async () => {
      const [receiveValue, transmitValue] = await Promise.all([
        getInstantValue(PROMETHEUS_QUERIES.NETWORK.RECEIVE),
        getInstantValue(PROMETHEUS_QUERIES.NETWORK.TRANSMIT),
      ]);

      return {
        receiveCurrent: receiveValue / BYTES_TO_KB,
        transmitCurrent: transmitValue / BYTES_TO_KB,
        unit: 'KB/s',
      };
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    ...DEFAULT_QUERY_OPTIONS,
  });

  const detailQuery = useQuery<{
    receive: Pick<NetworkUsageData['receive'], 'history' | 'podHistory'>;
    transmit: Pick<NetworkUsageData['transmit'], 'history' | 'podHistory'>;
  }>({
    queryKey: [...monitoringKeys.networkPrefix(), 'detail'],
    queryFn: async () => {
      const now = Math.floor(Date.now() / 1000);

      const [receiveHistory, transmitHistory, receivePodHistory, transmitPodHistory] = await Promise.all([
        getRangeValues(PROMETHEUS_QUERIES.NETWORK.RECEIVE_HISTORY, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValues(PROMETHEUS_QUERIES.NETWORK.TRANSMIT_HISTORY, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValuesByPod(PROMETHEUS_QUERIES.NETWORK.RECEIVE_BY_POD, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValuesByPod(PROMETHEUS_QUERIES.NETWORK.TRANSMIT_BY_POD, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
      ]);

      const receivePodHistoryWithColors = assignOrderedPodSeriesColors(
        receivePodHistory.map((podData) => ({
          ...podData,
          data: podData.data.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
        }))
      );

      const transmitPodHistoryWithColors = assignOrderedPodSeriesColors(
        transmitPodHistory.map((podData) => ({
          ...podData,
          data: podData.data.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
        }))
      );

      return {
        receive: {
          history: receiveHistory.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
          podHistory: receivePodHistoryWithColors,
        },
        transmit: {
          history: transmitHistory.map((point) => ({ ...point, value: point.value / BYTES_TO_KB })),
          podHistory: transmitPodHistoryWithColors,
        },
      };
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data: NetworkUsageData | undefined = summaryQuery.data
    ? {
        receive: {
          current: summaryQuery.data.receiveCurrent,
          history: detailQuery.data?.receive.history ?? [],
          podHistory: detailQuery.data?.receive.podHistory ?? [],
        },
        transmit: {
          current: summaryQuery.data.transmitCurrent,
          history: detailQuery.data?.transmit.history ?? [],
          podHistory: detailQuery.data?.transmit.podHistory ?? [],
        },
        unit: summaryQuery.data.unit,
      }
    : undefined;

  return {
    ...summaryQuery,
    data,
    isFetching: summaryQuery.isFetching || detailQuery.isFetching,
    isLoading: summaryQuery.isLoading,
  };
};
